/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Chip,
    Tabs,
    Tab,
    Box,
    Button,
} from "@mui/material";
import { Preview, EditNote } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useAuth } from "auth/Auth";

function TabPanel({ children, value, index }) {
    if (value !== index) return null;
    return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const INBOX_SETUP_MESSAGE =
    "Email inbox is not setup. Connect Gmail or Outlook in Settings → Inbox first.";

const wrapPreviewHtml = (htmlOrFragment) => {
    const raw = String(htmlOrFragment || "").trim();
    if (!raw) return "";
    if (/<html[\s>]/i.test(raw) || /<!doctype/i.test(raw)) return raw;
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head><body style="margin:0;padding:16px;background:#f9fafb;">${raw}</body></html>`;
};

export default function SendMerchantAllocationEmailModal({
    open,
    onClose,
    allocationId,
    allocationName,
    onSent,
}) {
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [inboxReady, setInboxReady] = useState(true);
    const [emailTab, setEmailTab] = useState(0);
    const [requestExpiresAt, setRequestExpiresAt] = useState(null);
    const [email, setEmail] = useState({
        to: "",
        subject: "",
        content: "",
        html: "",
    });

    useEffect(() => {
        if (!open || !allocationId) return undefined;
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError("");
            setInboxReady(true);
            setEmailTab(0);
            try {
                const inboxPromise = axiosInstance.get("/email-settings").catch((err) => {
                    if (err?.response?.status === 404) return { data: null };
                    throw err;
                });
                const draftPromise = axiosInstance.get(
                    `/merchant-allocations/${allocationId}/request-email-draft`
                );

                const [inboxRes, draftRes] = await Promise.all([inboxPromise, draftPromise]);
                if (cancelled) return;

                const hasInbox = Boolean(inboxRes?.data?.email_provider);
                setInboxReady(hasInbox);
                if (!hasInbox) {
                    setError(INBOX_SETUP_MESSAGE);
                }

                const draft = draftRes.data || {};
                setRequestExpiresAt(draft.request_expires_at || null);
                setEmail({
                    to: draft.to || "",
                    subject: draft.subject || "",
                    content: draft.content || "",
                    html: draft.html || draft.content || "",
                });
            } catch (err) {
                if (!cancelled) {
                    const detail = err?.response?.data?.detail;
                    setError(
                        (typeof detail === "string" && detail.trim())
                        || err?.message
                        || "Unable to load allocation request email."
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [open, allocationId, axiosInstance]);

    const previewHtml = useMemo(
        () => wrapPreviewHtml(email.html || email.content),
        [email.html, email.content]
    );

    const goToInboxSetup = () => {
        onClose?.();
        navigate("/inbox");
    };

    const handleCustomizeContent = (value) => {
        setEmail((prev) => ({
            ...prev,
            content: value,
            html: value,
        }));
    };

    const handleSend = async () => {
        if (!email.to?.trim()) {
            setError("Recipient email is required.");
            return;
        }
        if (!email.subject?.trim()) {
            setError("Subject is required.");
            return;
        }
        if (!String(email.content || email.html || "").trim()) {
            setError("Email content is required.");
            return;
        }
        if (!inboxReady) {
            setError(INBOX_SETUP_MESSAGE);
            return;
        }

        setSending(true);
        setError("");
        try {
            const configRes = await axiosInstance.get("/email-settings");
            if (!configRes.data?.email_provider) {
                setInboxReady(false);
                throw new Error(INBOX_SETUP_MESSAGE);
            }

            const body = email.html || email.content;
            await axiosInstance.post("/emails/send", {
                to: email.to,
                subject: email.subject,
                content: body,
                attachments: [],
                metadata: {
                    parent_type: "merchant_allocation",
                    parent_id: allocationId,
                    allocation_request: true,
                },
            });

            await axiosInstance.post(
                `/merchant-allocations/${allocationId}/confirm-request-email`,
                {
                    success: true,
                    request_expires_at: requestExpiresAt,
                }
            );

            onSent?.();
            onClose?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const detailText = typeof detail === "string" ? detail : "";
            const lower = `${detailText} ${err?.message || ""}`.toLowerCase();
            let message = detailText || err?.message || "Failed to send email.";
            if (
                lower.includes("not configured")
                || lower.includes("inbox")
                || err?.response?.status === 404
            ) {
                setInboxReady(false);
                message = INBOX_SETUP_MESSAGE;
            }
            setError(message);
            try {
                await axiosInstance.post(
                    `/merchant-allocations/${allocationId}/confirm-request-email`,
                    {
                        success: false,
                        error: message,
                        request_expires_at: requestExpiresAt,
                    }
                );
            } catch (_) {
                // non-fatal tracking update
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Send allocation request</DialogTitle>
            <DialogContent dividers>
                {error ? (
                    <Alert
                        severity={inboxReady ? "error" : "warning"}
                        sx={{ mb: 2 }}
                        onClose={() => setError("")}
                        action={
                            !inboxReady ? (
                                <Button color="inherit" size="small" onClick={goToInboxSetup}>
                                    Set up Inbox
                                </Button>
                            ) : null
                        }
                    >
                        {error}
                    </Alert>
                ) : null}

                {loading ? (
                    <MDBox display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </MDBox>
                ) : (
                    <>
                        <MDBox display="flex" flexWrap="wrap" gap={1} mb={2}>
                            <Chip label="Merchant allocation" color="info" size="small" />
                            {allocationName ? (
                                <Chip label={allocationName} size="small" variant="outlined" />
                            ) : null}
                            <Chip label="Accept / Reject links included" size="small" />
                        </MDBox>

                        <Alert severity="info" sx={{ mb: 2 }}>
                            Review the request email below. The merchant can accept or reject from the links in this message.
                        </Alert>

                        <TextField
                            fullWidth
                            label="To"
                            value={email.to}
                            onChange={(e) => setEmail((prev) => ({ ...prev, to: e.target.value }))}
                            margin="normal"
                        />

                        <TextField
                            fullWidth
                            label="Subject"
                            value={email.subject}
                            onChange={(e) => setEmail((prev) => ({ ...prev, subject: e.target.value }))}
                            margin="normal"
                        />

                        <MDBox mt={2}>
                            <Tabs
                                value={emailTab}
                                onChange={(_, v) => setEmailTab(v)}
                            >
                                <Tab icon={<Preview fontSize="small" />} iconPosition="start" label="Preview" />
                                <Tab icon={<EditNote fontSize="small" />} iconPosition="start" label="Customize" />
                            </Tabs>

                            <TabPanel value={emailTab} index={0}>
                                <MDTypography variant="caption" color="text" display="block" mb={1}>
                                    This is how the email will appear to the merchant.
                                </MDTypography>
                                <MDBox
                                    sx={{
                                        bgcolor: "#EEF3F9",
                                        borderRadius: "12px",
                                        border: "1px solid #E6E8F0",
                                        p: 1,
                                        maxHeight: 520,
                                        overflow: "hidden",
                                    }}
                                >
                                    <iframe
                                        title="Allocation request email preview"
                                        srcDoc={previewHtml}
                                        style={{
                                            width: "100%",
                                            minHeight: 560,
                                            border: "none",
                                            borderRadius: 8,
                                            background: "#EEF3F9",
                                        }}
                                    />
                                </MDBox>
                            </TabPanel>

                            <TabPanel value={emailTab} index={1}>
                                <TextField
                                    fullWidth
                                    label="Email body"
                                    value={email.content}
                                    onChange={(e) => handleCustomizeContent(e.target.value)}
                                    multiline
                                    minRows={12}
                                    margin="normal"
                                    helperText="You can edit the message before sending. Keep the Accept / Reject links so the merchant can respond."
                                />
                            </TabPanel>
                        </MDBox>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <MDButton color="secondary" onClick={onClose}>
                    Close
                </MDButton>
                {!loading ? (
                    <MDButton
                        variant="gradient"
                        color="info"
                        onClick={handleSend}
                        disabled={sending || !inboxReady}
                    >
                        {sending ? <CircularProgress size={20} color="inherit" /> : "Send email"}
                    </MDButton>
                ) : null}
            </DialogActions>
        </Dialog>
    );
}
